import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import { 
  LayoutDashboard, 
  Heart, 
  ShoppingBag, 
  FileText, 
  Settings, 
  User, 
  LogOut, 
  X, 
  Car,
  Tag,
  ChevronRight,
  Globe,
  Wrench
} from 'lucide-react';
import { motion } from 'framer-motion';

const Sidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: '/customer/dashboard' },
    { id: 'wishlist', label: 'Wishlist', icon: Heart, path: '/customer/dashboard/wishlist' },
    { id: 'orders', label: 'Orders', icon: ShoppingBag, path: '/customer/dashboard/orders' },
    { id: 'invoices', label: 'Invoices', icon: FileText, path: '/customer/dashboard/invoices' },
    { id: 'jobs', label: 'Service Jobs', icon: Wrench, path: '/customer/dashboard/jobs' },
    { id: 'deals', label: 'Deals', icon: Tag, path: '/customer/dashboard/deals' },
    { id: 'vehicles', label: 'My Vehicles', icon: Car, path: '/customer/dashboard/vehicles' },
    { id: 'profile', label: 'Profile', icon: User, path: '/customer/dashboard/profile' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/customer/dashboard/settings' },
  ];

  const isActive = (path) => {
    if (path === '/customer/dashboard') {
      return location.pathname === '/customer/dashboard' || location.pathname === '/customer/dashboard/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen bg-[#1e293b] z-50 w-72 transform transition-all duration-500 ease-[cubic-bezier(0.4, 0, 0.2, 1)] ${
          isOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
        } lg:translate-x-0 lg:sticky lg:top-0 shrink-0 border-r border-slate-800 shadow-xl`}
      >
        <div className="flex flex-col h-full">
          {/* Logo / Header Area */}
          <div className="px-6 py-8">
            <div className="flex items-center justify-between">
              <div 
                className="flex items-center gap-3 cursor-pointer group"
                onClick={() => navigate('/')}
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-red-500/20 blur-xl rounded-full scale-150 rotate-45 group-hover:bg-red-500/40 transition-colors" />
                  <img 
                    src="/sparepe.jpeg" 
                    alt="SparePe Logo" 
                    className="relative w-14 h-14 rounded-2xl object-cover bg-white shadow-premium p-0.5 group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-black text-white tracking-tighter leading-tight uppercase">SPARE PE</h2>
                  <p className="text-[10px] font-bold text-red-500 uppercase tracking-[0.2em]">Customer</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="lg:hidden w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 hover:bg-slate-800 transition-colors"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 overflow-y-auto px-4 py-2 scrollbar-hide">
            <div className="space-y-1.5">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => { navigate(item.path); setIsOpen(false); }}
                    className={`w-full group flex items-center justify-between px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-300 ${
                      active 
                        ? 'bg-red-600 text-white shadow-lg shadow-red-600/30' 
                        : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`transition-transform duration-300 group-hover:scale-110 ${active ? 'text-white' : 'text-slate-300 group-hover:text-red-500'}`}>
                        <Icon size={18} />
                      </div>
                      <span className="tracking-tight">{item.label}</span>
                    </div>
                    {active && (
                      <motion.div 
                        layoutId="activeIndRed" 
                        className="w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" 
                      />
                    )}
                    {!active && (
                      <ChevronRight size={14} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-red-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Footer Area */}
          <div className="p-4 mt-auto">
            <div className="bg-slate-800/50 rounded-3xl p-4 space-y-2 border border-slate-700 shadow-inner">
              <button
                type="button"
                onClick={() => { navigate('/'); setIsOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black text-slate-200 hover:bg-slate-700 hover:text-white transition-all uppercase tracking-widest"
              >
                <Globe size={16} />
                <span>Visit Store</span>
              </button>
              <button
                type="button"
                onClick={() => { logout(); navigate('/'); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-black text-slate-300 hover:bg-red-600 hover:text-white transition-all shadow-sm hover:shadow-red-200 uppercase tracking-widest"
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
